<template lang="pug">
	.jodit-editor.flex-col(:class="{ disabled }", v-if="!isReloading", :style="{ '--zoom': `${(zoom || 1) * 100}%`}")
		.flex-row.wrap.j-buttons
			.j-button-holder.flex-row.align-center.center(v-for="button in extraButtons.filter(f => !f.hiddenInRoot)", @mousedown.prevent="", @click.stop="processButton(button)", :class="[`jodit-ui-group__${button.name}`, button.groupEnd ? 'group-end' : '']")
				.j-button.clickable.flex-row.align-center.space(:id="`button_${button.name}`", :title="button.hint")
					fas.jodit-toolbar-button__icon(v-if="button.icon", :icon="button.icon")
					span(v-if="button.text") {{button.text}}
					fas(v-if="button.list || button.items", icon="angle-down", size="xs")
				ContextMenu(v-if="button.list || button.items", :ref="`menuButton_${button.name}`", :items="getButtonListItems(button)", @item-click="onItemClick", :target="`#button_${button.name}`")
					template(v-slot:item="{ data }")
						.jodit-editor--context-menu-item.flex-row.space.align-center(:title="data.text", :class="[`jodit-ui-group__${data.key}`]")
							fas.jodit-toolbar-button__icon(v-if="data.icon", :icon="data.icon")
							span {{data.text}}
		jodit-editor(
			v-model="innerSSML",
			ref="joditEditor",
			:height="height"
			:config="{ ...defaultConfig, ...(config || {}), placeholder: '' }",
			:buttons="[]",
			:extraButtons="[]"
		)
</template>

<script>
	import _ from 'lodash';
	import $ from 'jquery';
	import ssmlHelper from './ssml-helper';
	import DxDropDownButton from 'devextreme-vue/drop-down-button';
	import ContextMenu from 'app-core/ContextMenu.vue';

	export default {
		translationCategory: 'insertion.JoditEditor',
		name: 'audio-tracker-insertion--jodit-editor',
		components: { DxDropDownButton, ContextMenu },
		props: ['config', 'ssml', 'buttons', 'extraButtons', 'isRTL', 'disabled', 'zoom', 'height'],
		model: {
			prop: 'ssml',
			event: 'change'
		},
		data() {
			return {
				innerSSML: this.formatJoditHtml(this.ssml || '', true),
				defaultConfig: {
					beautifyHTML: false,
					processPasteHTML: false,
					addNewLine: false,
					addNewLineOnDBLClick: false,
					enableDragAndDropFileToEditor: false,
					spellcheck: false,
					cleanHTML: {
						removeEmptyElements: false,
						fillEmptyParagraph: false,
						replaceNBSP: true,
						replaceOldTags: {},
						allowTags: false,
						denyTags: false
					},
					enter: 'div',
					saveSelectionOnBlur: true
				},
				isInnerFormatting: false,
				isReloading: false
			}
		},
		computed: {
			editor: {
				get: function() {
					return this.$refs.joditEditor.editor;
				},
				cache: false
			},
			editorWysiwyg() {
				return $(this.editor.container).find('.jodit-wysiwyg');
			}
		},
		mounted() {
			if (__DEBUG__) {
				console.debug('Jodit editor mounted');
				window.joditEditor = this;
			}

			this.initEditor();

			this.$processSelectionChange = _.debounce(this.processSelectionChange, 1000, {
				leading: false,
				trailing: true
			});

			this.$el.addEventListener('selectionchange', this.$processSelectionChange);
			window.addEventListener('mouseup', this.processSelectionChange, false);
		},
		destroyed() {
			this.$el.removeEventListener('selectionchange', this.$processSelectionChange);
			window.removeEventListener('mouseup', this.processSelectionChange);

			delete this.$processSelectionChange;

			this.stopMutationObserver();

			delete this.$preventObserving;
		},
		watch: {
			innerSSML(v) {
				this.isInnerFormatting = true;
				this.$emit('change', ssmlHelper.clearPreformat(v));
				setImmediate(() => this.isInnerFormatting = false);
			},
			ssml(v) {
				if (!this.isInnerFormatting) {
					this.innerSSML = v;
				}
			},
			height() {
				this.reloadEditor();
			}
		},
		methods: {
			handleScroll(e) {
				if (__DEBUG__)
					console.debug('Scroll', { e });
			},
			editorActionRemoved(actionElem) {
				if (this.$preventObserving) return;
				this.$nextTick(() => {
					let elem = document.querySelector(`[action-source-id="${actionElem.getAttribute('action-id')}"]`);
					if (__DEBUG__)
						console.debug('Process editor action', actionElem, elem);
					
					if (elem) {
						elem.after(...elem.childNodes);
						elem.remove();
					}

					if (actionElem.parentNode) {
						let next = this.editor.selection.setCursorAfter(actionElem.nextSibling);
						actionElem.remove();
						if (next) {
							this.editor.selection.setCursorAfter(next);
						}
					}
				});
			},
			initMutationObserver() {
				this.stopMutationObserver();

				const startObserving = (domNode, tagName) => {
					const observer = new MutationObserver(mutations => {
						mutations.forEach((mutation) => {
							const elementsRemoved = Array.from(mutation.removedNodes).filter(element => (element.tagName || '').toLowerCase() === tagName.toLowerCase());

							if (elementsRemoved?.length) {
								if (__DEBUG__)
									console.debug('Process editor action removed');
								elementsRemoved.map(f => this.editorActionRemoved(f))
							} else if ((mutation.target.tagName || '').toLowerCase() === tagName.toLowerCase() && mutation.target.innerText?.toLowerCase() !== mutation.target.getAttribute('inner-text')?.toLowerCase()) {
								if (__DEBUG__)
									console.debug('Process editor action changed', { in:  mutation.target.innerText, tt: mutation.target.getAttribute('inner-text') });
								mutation.target.remove();
							}
						});
					});

					observer.observe(domNode, {
						childList: true,
						attributes: true,
						characterData: true,
						subtree: true,
					});

					return observer;
				};

				this.$editorActionsObserver = startObserving(document.body, 'editor-action');
			},
			stopMutationObserver() {
				if (this.$editorActionsObserver) {
					this.$editorActionsObserver.disconnect();
				}
				delete this.$editorActionsObserver;
			},
			onItemClick(e) {
				if (__DEBUG__)
					console.debug('onItemClick', e);
				const button = e.itemData.button;

				if (button.exec) {
					button.exec(this.editor, button, { 
						control: {
							args: [e.itemData.key]
						},
						item: e?.itemData
					});
				}
			},
			getButtonListItems(button) {
				let list = button?.list || [];

				if (button?.list && !Array.isArray(button.list)) {
					list = Object.keys(button.list).map(f => ({
						key: f,
						text: button.list[f]
					}))
				}

				if (button.items) {
					list = button.items.map(f => ({
						text: f.text,
						icon: f.icon,
						key: f.key || f.name,
						button: f,
						items: f.list || f.items ? this.getButtonListItems(f) : []
					}));
				}

				return (list || []).map(f => ({
					...f,
					text: f?.text || f,
					template: 'item',
					key: f?.key || f,
					button: f.button || button
				}));
			},
			processButton(button) {
				if (!button) return;
				if (button.list || button.items) {
					this.$refs[`menuButton_${button.name}`][0].show();
				} else {
					button.exec(void 0, void 0, { control: button });
				}
			},
			initEditor() {
				let editor = this.editor;

				this.initMutationObserver();

				$(editor.container)
					.find('.jodit-wysiwyg')
					.click((e) => {
						if (__DEBUG__)
							console.debug('Jodit element click', { e });

						$(editor.container)
							.find('.selected')
							.removeClass('selected');

						let target = $(e.target);

						let processed = [];
						let processSelected = (cur) => {
							if (processed.includes(cur)) return;
							processed.push(cur);
							cur.classList.add('selected');
							if (cur.parentNode) {
								ssmlHelper.tagsList.map(f => {
									let item = cur.parentNode.closest(f.replace(/:/g, '\\:'));
									if (item?.classList) {
										processSelected(item);
									}
								});
							}
						}
						
						processSelected(e.target);

						if (e.target?.nodeName?.toLowerCase() === 'editor-action') {
							let parentNode = e.target.parentNode;

							if (__DEBUG__)
								console.debug('Editor action', { target, parentNode });

							this.$emit('click', e.target.getAttribute('action-tag'), e.target.getAttribute('action-attribute'), parentNode, e);
						}
					})
					.on('mousewheel', (e) => {
						editor.container.querySelector('.jodit-wysiwyg').scrollBy(0, e?.originalEvent?.wheelDeltaY * -1 || 0)
					})
					.on('mouseover', () => {
						editor.container.querySelector('.jodit-wysiwyg').focus();
					})
					.on('dblclick', this.onJoditContainerDblClick);

				editor.events
					.on('beforePaste', this.onJoditBeforePaste)
					.on('beforeEnter', this.onJoditBeforeEnter);

				this.prepareExtraButtons();
				setTimeout(() => this.calcFormat(true), 500);
			},
			onJoditContainerDblClick(e) {
				const selectedText = `${this.editor.selection.html}`
					.replace(new RegExp('&nbsp;', 'g'), ' ');

				if (selectedText.endsWith(' ')) {
					let spacesCount = 1;
					while (selectedText[selectedText.length - 1 - spacesCount] === ' ') {
						spacesCount++;
					}

					this.editor.selection.range.setEnd(this.editor.selection.range.endContainer, this.editor.selection.range.endOffset - spacesCount);
				}
			},
			onJoditBeforeEnter(e) {
				if (__DEBUG__)
					console.debug('beforeEnter', e);
				this.editor.selection.insertHTML('<p></p>');
				return false;
			},
			onJoditBeforePaste(e) {
				let clpHtml = `${e?.clipboardData?.getData('text/html')}`;
				let clpText = `${e?.clipboardData?.getData('text/plain')}`;
				let text = clpHtml || clpText;

				if (clpText.indexOf('<speak ') > -1 || clpText.indexOf('<span class="voice"')) {
					text = clpText;
				}

				let hasSpeakTag = text.indexOf('<speak ') > -1;

				if (__DEBUG__)
					console.debug('On paste', { text, e });
				if (!text) return false;
				if (!hasSpeakTag) {
					if (text.indexOf('<span class="voice"') > -1) {
						text = text
							.replace(new RegExp('<span class="voice"', 'g'), '\r\n<span class="voice"');
						text = $('<div>').append(text).text();
					} else {
						text = e?.clipboardData?.getData('text/plain') || text;
						if (__DEBUG__)
							console.debug('Clipboard text', {
								text,
								clipboardText: e?.clipboardData?.getData('text/plain')
							});
					}
					text =
						text.replace(/\n/g, '<p></p>')
							.replace(/\t/g, ' ')
							.replace(/\r/g, '');
					this.editor.selection.insertHTML(text);
					this.$emit('paste', text);
				} else {
					this.$emit('paste', text, true);
				}
				return false;
			},
			formatJoditHtml(v, preformat) {
				if (preformat) {
					v = ssmlHelper.preformatSsml(v);
				}
				this.isInnerFormatting = true;
				try {
					let htmlDoc = $('<div>').append(v);
					ssmlHelper.clearSsml(htmlDoc[0]);
					if (!htmlDoc.text().length) {
						htmlDoc.find('voice').prepend('&nbsp;');
					}
					htmlDoc.find('voice').prepend('&nbsp;').append('&nbsp;');

					while(htmlDoc[0]?.childNodes[0]?.nodeName?.toLowerCase() === 'div') {
						htmlDoc = $(htmlDoc[0].innerHTML);
					}

					v = htmlDoc[0] && htmlDoc[0].innerHTML
						.replace(/(&nbsp;){2,}/g, '&nbsp;') || '';
				} finally {
					setImmediate(() => {
						if (this._isDestroyed) return;
						this.isInnerFormatting = false;
					});
				}
				return v;
			},
			calcFormat(preformat) {
				if (__DEBUG__)
					console.debug('calcFormat');
				this.innerSSML = this.formatJoditHtml(this.innerSSML, preformat);
			},
			preformat() {
				this.$nextTick(() => {
					this.$preventObserving = true;
					let elem = this.$el.querySelector('.jodit-wysiwyg');
					ssmlHelper.preformatDom(elem);
					this.$nextTick(() => this.$nextTick(() => this.$preventObserving = false));
				});
			},
			prepareExtraButtons() {
				(this.extraButtons || []).map(b => {
					let btn = this.editor.toolbar.buttons.filter(f => f.name === b.name)[0];
					if (btn) {
						btn.state.icon.name = b.icon;
					}
				});
			},
			reloadEditor() {
				if (__DEBUG__)
					console.debug('Reload');
				this.isReloading = true;
				this.$nextTick(() => {
					this.isReloading = false;
					this.$nextTick(() => {
						if (this._isDestroyed) return;
						this.initEditor();
					});
				});
			},
			processSelectionChange() {
				this.getSelectedText();
			},
			getSelectedText() {
				let selection = window.getSelection();
				if (selection.rangeCount === 0) return '';
				var t = '';
				
				let rng = window.getSelection().getRangeAt(0);
				this.expandToWord(rng);
				t = rng.toString();

				return t.replace(/\r?\n/g, ' ').replace(/\s+/g, ' ').replace(/^\W+|\W+$/g, '');
			},
			expandToWord(range) {
				if (range.collapsed)
				{
					return;
				}

				let stopMatch = /[!?.,:;_<>\[\]"'*\\/\s\n&%=\-\+#^()]/;

				let processed = false;
				while (range.startOffset > 0 && !range.toString()[0]?.match(stopMatch))
				{
					range.setStart(range.startContainer, range.startOffset - 1);
					processed = true;
				}
				if (processed && range.startOffset !== 0) {
					range.setStart(range.startContainer, range.startOffset + 1);
				}

				processed = false;
				while (range.endOffset < range.endContainer.length && !range.toString()[range.toString().length - 1]?.match(stopMatch))
				{
					range.setEnd(range.endContainer, range.endOffset + 1);
					processed = true;
				}
				if (processed && range.endOffset < range.endContainer.length) {
					range.setEnd(range.endContainer, range.endOffset - 1);
				}

				if (range.toString()[0] === ' ') {
					range.setStart(range.startContainer, range.startOffset + 1);
				}

				if (range.toString().slice(-1)[0] === ' ') {
					range.setEnd(range.endContainer, range.endOffset - 1);
				}
			},
			prepareSsml(ssml) {

			}
		}
	};
</script>

<style lang='scss'>
	.jodit-editor {
		&.mod--rtl {
			.jodit-toolbar-button__icon:not(:empty) + .jodit-toolbar-button__text:not(:empty) {
				margin: 0 !important;
				margin-right: 8px !important;
			}
		}

		&.disabled {
			opacity: 0.5;
			pointer-events: none;
		}

		.toolbar-separator {
			height: 36px;
			margin: 0 5px;
			border-left: 1px solid #ddd;
		}

		.toolbar-menu-separator {
			height: 1px;
			border-bottom: 1px solid #ddd;
		}

		.j-button-holder {
			min-width: 33px;
			min-height: 33px;

			&.group-end {
				border-right: 1px solid #efefef;
			}

			&:hover {
				background: #e9e9e9;
			}
		}

		.j-buttons {
			background: #f9f9f9;
			border: 1px solid #dadada;
			border-bottom: 0px;
			border-radius: 4px 4px 0px 0px;
			
			background-size: 40px 37.5px;
			background-image: repeating-linear-gradient(#f9f9f9, #f9f9f9 36px, #dedede 36px, #dedede 37px)
		}

		.j-button {
			//border: 1px solid #dedede;
			border-radius: 2px;
			padding: 6px 8px;
			margin: 2px;

			
		}

		.jodit-container {
			border-top: 0px;
			border-radius: 0px 0px 4px 4px;
			font-size: var(--zoom);
		}
	}

	.jodit-editor--context-menu-item {
		padding: 6px 12px;
		border-bottom: 1px solid #efefef;
		font-size: 12px;
	}
</style>
